import PropTypes from 'prop-types';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TextField } from '@material-ui/core';

const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

// Format a "YYYY-MM" string into a human-readable label like "August 2026".
export const formatMonthLabel = (month) => {
  if (!month || typeof month !== 'string') return '';
  const [year, monthIndex] = month.split('-');
  const idx = parseInt(monthIndex, 10) - 1;
  if (idx < 0 || idx > 11) return month;
  return `${MONTH_LABELS[idx]} ${year}`;
};

// Current month in YYYY-MM format (e.g. "2026-08").
export const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Normalize a month value into YYYY-MM format. Accepts either a Date instance
// (what the MUI DatePicker emits) or an already-formatted "YYYY-MM" string.
// Returns null for falsy/invalid inputs so the caller can skip the param.
export const formatMonthParam = (month) => {
  if (!month) return null;
  if (month instanceof Date) {
    if (Number.isNaN(month.getTime())) return null;
    return `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
  }
  if (typeof month === 'string') {
    const match = month.match(/^(\d{4})-(\d{1,2})$/);
    if (!match) return null;
    return `${match[1]}-${match[2].padStart(2, '0')}`;
  }
  return null;
};

// Read a `?month=YYYY-MM` query param and turn it into a Date (first of the
// month). Falls back to `new Date()` for missing / malformed values so callers
// can pass the result straight to <MonthPicker value={...} /> without
// branching on the param shape. Accepts any object with a `.get('month')`
// method (e.g. the array returned by `useSearchParams()`).
export const initialMonthFromUrl = (searchParams) => {
  const raw = searchParams?.get ? searchParams.get('month') : null;
  if (!raw) return new Date();
  const match = raw.match(/^(\d{4})-(\d{1,2})$/);
  if (!match) return new Date();
  const year = parseInt(match[1], 10);
  const monthIndex = parseInt(match[2], 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return new Date();
  return new Date(year, monthIndex, 1);
};

// Generate a list of recent months in YYYY-MM format, newest first.
// Defaults to 12 months ending with the current month.
export const generateBillingMonths = (count = 12) => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
};

MonthPicker.propTypes = {
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func
};

MonthPicker.defaultProps = {
  value: null,
  onChange: () => {}
};

export default function MonthPicker({ value, onChange }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        label="Billing Month"
        views={['month', 'year']}
        value={value}
        onChange={onChange}
        disableFuture
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            sx={{ bgcolor: 'white' }}
            data-testid="billing-month"
          />
        )}
        sx={{ '& .MuiInputBase-input': { py: 1 } }}
      />
    </LocalizationProvider>
  );
}
